import React, { useState, useEffect } from 'react';
import { Save, CheckCircle, AlertTriangle, Settings, DollarSign, Calendar, Phone, HelpCircle, BookOpen, Package, Shield, Sparkles } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { supabase } from '../../lib/supabase';
import PricingConfigSection from './components/PricingConfigSection';
import ScheduleConfigSection from './components/ScheduleConfigSection';
import ContactConfigSection from './components/ContactConfigSection';
import FAQConfigSection from './components/FAQConfigSection';
import KnowledgeBaseSection from './components/KnowledgeBaseSection';
import ProductKnowledgeSection from './components/ProductKnowledgeSection';
import CoherenceRulesSection from './components/CoherenceRulesSection';
import ValidationTestingSection from './components/ValidationTestingSection';
import AIPreviewSection from './components/AIPreviewSection';
import Icon from '../../components/AppIcon';


export default function FatouAIConfigurationManagement() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('pricing');

  const [config, setConfig] = useState({
    pricing: {
      barrique: { baseRate: '', perKg: '', currency: 'EUR' },
      carton: { baseRate: '', perKg: '', currency: 'EUR' },
      meuble: { baseRate: '', perKg: '', currency: 'EUR' },
      mix: { baseRate: '', perKg: '', currency: 'EUR' },
      vehicule: { baseRate: '', currency: 'EUR' },
      conteneur: { baseRate: '', currency: 'EUR' }
    },
    promotions: {
      active: '',
      seasonal: '',
      conditions: ''
    },
    schedule: {
      nextDeparture: '',
      vessel: '',
      departurePort: 'Le Havre',
      arrivalPort: 'Abidjan',
      estimatedArrival: '',
      transitDays: 14
    },
    contact: {
      phone: '',
      whatsapp: '',
      email: 'contact@adtexpress.cloud',
      address: '',
      hours: 'Lun-Ven: 9h-18h'
    },
    faqs: [],
    knowledgeBase: {
      services: '',
      process: '',
      policies: '',
      specialNotes: ''
    },
    products: [],
    coherenceRules: []
  });

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      
      if (!supabase) {
        setMessage({ type: 'error', text: 'Configuration de la base de données non disponible' });
        setLoading(false);
        return;
      }

      // Load from a dedicated fatou_config table
      const { data, error } = await supabase
        ?.from('fatou_config')
        ?.select('*')
        ?.eq('is_active', true)
        ?.single();

      if (error && error?.code !== 'PGRST116') {
        console.error('Error loading configuration:', error);
      }

      if (data?.config_data) {
        setConfig(data?.config_data);
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement de la configuration' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfiguration = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      if (!supabase) {
        setMessage({ type: 'error', text: 'Configuration de la base de données non disponible' });
        return;
      }

      // Check if config exists
      const { data: existing } = await supabase
        ?.from('fatou_config')
        ?.select('id')
        ?.eq('is_active', true)
        ?.single();

      if (existing) {
        // Update existing
        const { error } = await supabase
          ?.from('fatou_config')
          ?.update({
            config_data: config,
            updated_at: new Date()?.toISOString()
          })
          ?.eq('id', existing?.id);

        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          ?.from('fatou_config')
          ?.insert({
            config_data: config,
            is_active: true
          });

        if (error) throw error;
      }

      setMessage({ type: 'success', text: 'Configuration enregistrée avec succès!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error saving configuration:', error);
      setMessage({ type: 'error', text: 'Erreur lors de l\'enregistrement' });
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'pricing', label: 'Tarifs & Prix', icon: DollarSign },
    { id: 'schedule', label: 'Dates & Navires', icon: Calendar },
    { id: 'contact', label: 'Contacts', icon: Phone },
    { id: 'faq', label: 'FAQ', icon: HelpCircle },
    { id: 'knowledge', label: 'Base de Connaissances', icon: BookOpen },
    { id: 'products', label: 'Produits', icon: Package },
    { id: 'coherence', label: 'Règles de Cohérence', icon: Shield },
    { id: 'validation', label: 'Validation & Tests', icon: CheckCircle },
    { id: 'preview', label: 'Aperçu IA', icon: Sparkles }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement de la configuration...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <Settings className="text-green-600" size={32} />
            <h1 className="text-3xl font-bold text-gray-900">Configuration Fatou IA</h1>
          </div>
          <p className="text-gray-600">Gérez les informations de l'assistante virtuelle Fatou</p>
        </div>

        {/* Message Alert */}
        {message?.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center space-x-2 ${
            message?.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {message?.type === 'success' ? (
              <CheckCircle size={20} />
            ) : (
              <AlertTriangle size={20} />
            )}
            <span>{message?.text}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-4 px-6" aria-label="Tabs">
              {tabs?.map((tab) => {
                const Icon = tab?.icon;
                return (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-3 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab?.id
                        ? 'border-green-600 text-green-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon size={18} />
                    <span>{tab?.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'pricing' && (
              <PricingConfigSection 
                config={config?.pricing} 
                onChange={(pricing) => setConfig({ ...config, pricing })}
              />
            )}
            
            {activeTab === 'schedule' && (
              <ScheduleConfigSection 
                config={config?.schedule} 
                onChange={(schedule) => setConfig({ ...config, schedule })}
              />
            )}
            
            {activeTab === 'contact' && (
              <ContactConfigSection 
                config={config?.contact} 
                onChange={(contact) => setConfig({ ...config, contact })}
              />
            )}
            
            {activeTab === 'faq' && (
              <FAQConfigSection 
                faqs={config?.faqs} 
                onChange={(faqs) => setConfig({ ...config, faqs })}
              />
            )}
            
            {activeTab === 'knowledge' && (
              <KnowledgeBaseSection 
                config={config?.knowledgeBase} 
                onChange={(knowledgeBase) => setConfig({ ...config, knowledgeBase })}
              />
            )}

            {activeTab === 'products' && (
              <ProductKnowledgeSection 
                products={config?.products} 
                onChange={(products) => setConfig({ ...config, products })}
              />
            )}

            {activeTab === 'coherence' && (
              <CoherenceRulesSection 
                rules={config?.coherenceRules} 
                onChange={(coherenceRules) => setConfig({ ...config, coherenceRules })}
              />
            )}

            {activeTab === 'validation' && (
              <ValidationTestingSection config={config} />
            )}

            {activeTab === 'preview' && (
              <AIPreviewSection />
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSaveConfiguration}
            disabled={saving}
            className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={20} />
            <span>{saving ? 'Enregistrement...' : 'Enregistrer la Configuration'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}